import { AxiosResponse } from 'axios'
import { LoadingAnimation } from '@outbound-ai/ursa'
import { batchUpload } from 'features/autocheck/autoCheckApi'
import { BatchType } from 'features/autocheck/types'
import {
  ClaimErrorProblemTypeEnum,
  ClaimErrorTableHeaderEnum,
  IClaimError,
  ICSVImportPostResponse,
  IFileUploadError,
} from 'features/claims/types'
import { countBy, keys, sortBy, values } from 'lodash'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { IColumn, IRow } from 'types/tableTypes'

// paths
// components
// types and utils
// features
export interface IProps {
  file: File | null
  // data: ICSVImportPostResponse | null
  // error: IFileUploadError | null
  loading: boolean
  onIsLoading: (status: boolean) => void
  onSetData: (data: ICSVImportPostResponse | null) => void
  onSetError: (error: IFileUploadError | null) => void
  onRemoveFile: () => void
  batchType: BatchType
  repeatedCalls: boolean
}

const headerData = () => {
  return keys(ClaimErrorTableHeaderEnum).map((key: string) => {
    const tableHeaderValue = ClaimErrorTableHeaderEnum[key as keyof typeof ClaimErrorTableHeaderEnum]
    const label = tableHeaderValue === ClaimErrorTableHeaderEnum.line ? 'row' : tableHeaderValue
    return {
      name: key,
      tooltip: key,
      label,
    } as IColumn
  })
}

export const BatchUploadButton = ({
  file,
  loading,
  onIsLoading,
  onSetData,
  onSetError,
  batchType,
  repeatedCalls,
}: IProps) => {
  const cleanUp = () => {
    onSetData(null)
    onSetError(null)
  }

  const fileUploadHandler = async () => {
    // if (data && !error && cleanUp()) return navigate(PathsEnum.batch + '/' + PathsEnum.batchProcessing)
    // if (!data && error && cleanUp()) return onRemoveFile()
    cleanUp()
    if (!file) return
    onIsLoading(true)
    batchUpload(file, batchType, repeatedCalls)
      .then((response: AxiosResponse<ICSVImportPostResponse>) => {
        onSetData(response.data)
        onIsLoading(false)
      })
      .catch((err: { response: { code: number; message: string; data: ICSVImportPostResponse } }) => {
        onIsLoading(false)
        if (err?.response?.data?.csvImportProblems?.length) {
          const failedClaimsCount = values(
            countBy(err.response.data.csvImportProblems, (element) => element.line),
          ).length

          const errorHeaderData = headerData()
          const errorRowsData: IRow[] = sortBy(err.response.data.csvImportProblems, (e) => e.line).map(
            (field: IClaimError) => {
              const row: Partial<IRow> = { [ClaimErrorTableHeaderEnum.line]: field.line }
              switch (field.problemType) {
                case ClaimErrorProblemTypeEnum.line:
                  row[ClaimErrorTableHeaderEnum.column] = 'N/A'
                  row[ClaimErrorTableHeaderEnum.problem] = field.detail
                  break
                case ClaimErrorProblemTypeEnum.column:
                  row[ClaimErrorTableHeaderEnum.column] = field.column || '-'
                  row[ClaimErrorTableHeaderEnum.problem] = `"${field.instance}" is not valid`
                  break
              }
              const tooltips = keys(row).reduce((acc, key: string) => ({ ...acc, [`${key}-tooltip`]: row[key] }), {})
              return { ...row, ...tooltips } as IRow
            },
          )

          return onSetError({
            claimCount: err.response.data.claimCount,
            missingColumns: err.response.data.missingColumns,
            failedClaimCount: failedClaimsCount,
            processedClaimCount: err.response.data.claimCount - failedClaimsCount,
            data: {
              columns: errorHeaderData,
              rows: errorRowsData,
            },
          })
        }

        onSetError({
          detail: err?.response?.data?.detail || 'There was a problem validating your claim file.',
          missingColumns: err?.response?.data?.missingColumns || [],
        })
      })
  }

  return (
    <Div>
      <Button
        data-testid="file-upload-btn"
        variant="info"
        radius="rounded"
        className="btn-validate"
        disabled={!file}
        size="large"
        color="info"
        onClick={fileUploadHandler}
      >
        {loading ? (
          <>
            <LoadingAnimation variant="light" />
            <span>Processing...</span>
          </>
        ) : (
          `Validate`
        )}
      </Button>
    </Div>
  )
}

const Div = styled.div`
  width: 100%;
  .btn-start-over {
    width: 100%;
  }
`
